import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { FaInfoCircle } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import Checkbox from '@/shared/components/input/Checkbox';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, CopyIcon, DeleteIcon, IconPencil } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';
import ToastrService from '../../../shared/services/ToastrService';
import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete, CanUpdate } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import dataCacheService from '@/shared/services/DataCacheService';
import TextSearch from '@/shared/components/input/TextSearch';

import messages from './InteractionSettingsMessages';
import { parse } from 'filepond';

class InteractionTemplateOverview extends Component {
  state = {
    templates: null,
    loading: true,
    companyId: null,
    deleteTemplate: null,
  };

  componentDidMount() {
    const { intl, type } = this.props;
    const companyId = dataCacheService.getDefaultCompany().id;
    api.base
      .get(`${config.appUrls.api}/api/InteractionTemplate/Company/${type}`)
      .then(({ data }) => {
        this.setState({
          templates: data,
          loading: false,
          companyId,
        });
      });
  }

  addTemplate = () => {
    const { stateService, type } = this.props;
    stateService.gotoState('settings.interactiontemplatesform.new', {
      type: type,
    });
  };

  editTemplate = template => {
    const { stateService, type } = this.props;
    const { companyId } = this.state;
    stateService.gotoState('settings.interactiontemplatesform.edit', {
      templateId: template.id,
      companyId: companyId,
      type: type,
    });
  };

  deleteTemplate = template => {
    if (!template || !template.id) return;
    api.base.delete(`${config.appUrls.api}/api/interactiontemplate/template/${template.id}`).then(
      () => {
        this.setState(({ templates }) => ({
          templates: templates.filter(t => t.id !== template.id),
          deleteTemplate: null,
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  copyTemplate = template => {
    if (!template || !template.id) return;
    api.base.post(`${config.appUrls.api}/api/interactiontemplate/copy/${template.id}`).then(
      ({ data }) => {
        const newTemplate = data;
        const { templates } = this.state;
        const { intl } = this.props;

        const sourceTemplateIndex = templates.findIndex(t => t.id === template.id);
        if (sourceTemplateIndex >= 0) {
          // Create a new array to hold the updated templates list
          const updatedTemplates = [
            ...templates.slice(0, sourceTemplateIndex + 1), // Copy elements before the source template (including the source template)
            newTemplate, // Add the copied template after the source template
            ...templates.slice(sourceTemplateIndex + 1), // Copy elements after the source template
          ];
          // Update state with the new templates array
          this.setState({
            templates: updatedTemplates,
          });
        }
        ToastrService.changesSaved(intl);
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  render() {
    const { templates, loading, deleteTemplate, search, languages, languageCode } = this.state;
    const { intl, stateService, tab } = this.props;

    const canUpdateTemplates = authService.hasPermission({
      functionality: ['Settings', 'EmailAndInteractionTemplates'],
      privilege: privileges.Update,
    });

    return (
      <div className="panel">
        <div className="panel-heading">
          <CanCreate on={['Settings', 'EmailAndInteractionTemplates']}>
            <div className="panel-buttons">
              <button type="button" className="btn btn-inverse" onClick={() => this.addTemplate()}>
                <i className="fa fa-plus" /> <FormattedMessage {...messages.Add} />
              </button>
            </div>
          </CanCreate>
        </div>
        {!loading && templates ? (
          <div className="panel-body container-fluid">
            <table className="table table-grid table-hover margin5T">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage {...messages.TemplateName} />
                  </th>
                  <th>
                    <FormattedMessage {...messages.TemplateLanguages} />
                  </th>
                  <th className="text-right">
                    <FormattedMessage {...messages.Actions} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates != undefined &&
                  templates.map(template => (
                    <tr key={template.id}>
                      <td>
                        {intl.formatMessage(listItemMsg('template', template.id, template.name))}
                      </td>
                      <td>{template.languages.map(lang => lang.languageCode).join(', ')}</td>
                      <td className="text-right">
                        <a
                          onClick={() => this.editTemplate(template)}
                          className="btn btn-xs btn-icon"
                        >
                          <IconPencil />
                        </a>
                        <a
                          onClick={() => this.copyTemplate(template)}
                          className="btn btn-xs btn-icon"
                        >
                          <CopyIcon />
                        </a>
                        <CanDelete on={['Settings', 'EmailAndInteractionTemplates']}>
                          <a
                            onClick={() => this.setState({ deleteTemplate: template })}
                            className="btn btn-xs btn-icon"
                          >
                            <DeleteIcon />
                          </a>
                        </CanDelete>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {deleteTemplate !== null && (
              <YesNoModal
                intl={intl}
                show
                title={intl.formatMessage(messages.DeleteTemplate)}
                message={intl.formatMessage(messages.DeleteTemplateConfirmationMsg)}
                onNo={() =>
                  this.setState(state => ({
                    ...state,
                    deleteTemplate: null,
                  }))
                }
                onSave={() => this.deleteTemplate(deleteTemplate)}
              />
            )}
          </div>
        ) : null}
        {loading && <LoadingAnimation />}
      </div>
    );
  }
}

InteractionTemplateOverview.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  tab: PropTypes.number.isRequired,
  type: PropTypes.number.isRequired,
};

export default injectStateService(injectIntl(InteractionTemplateOverview));
